import React, { useState } from 'react';
import DataTable from 'aldoo-ra/DataTable';
import Button from 'aldoo-ra/Button';
import Modal from 'aldoo-ra/Modal';
import DropdownSelect from 'aldoo-ra/DropdownSelect';
import { XCircleIcon, ClipboardDocumentIcon, PlayIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { showPromptDialog } from 'aldoo-ra/PromptDialog';
import { showNotification } from 'aldoo-ra/Notification';
import { GetTasks } from './task-api';
import useTaskManager from './hooks/useTaskManager';
import LiveUptime from './components/LiveUptime';
import TaskDetailsPanel from './components/TaskDetailsPanel';
import RunScriptDialog from './components/RunScriptDialog';
import GlobalEnvDialog from './components/GlobalEnvDialog';
import { getStatusColor, STATUS_FILTER_OPTIONS, SUSPENDED_FILTER_OPTIONS } from './utils/taskConstants';

export default function TaskManagerOverview() {
    const {
        connectionStatus,
        selectedTask,
        setSelectedTask,
        selectedTaskRef,
        refreshKey,
        setRefreshKey,
        filters,
        setFilters,
        isInitialized,
        serverTimeOffset,
        controller,
        requestFields
    } = useTaskManager();

    const [activeTab, setActiveTab] = useState('details');
    const [showRunDialog, setShowRunDialog] = useState(false);
    const [showGlobalEnvDialog, setShowGlobalEnvDialog] = useState(false);

    const handleStop = async (task) => {
        showPromptDialog({
            title: "Stop Task",
            message: `Are you sure you want to stop task "${task.name}"?`,
            confirmText: "Stop Task",
            cancelText: "Cancel",
            onConfirm: async () => {
                try {
                    await controller.stopTask(task.id);
                    setRefreshKey(prev => prev + 1);
                    showNotification({
                        message: `Task "${task.name}" stopped successfully`,
                        type: 'success'
                    });
                } catch (e) {
                    console.error("Failed to stop task", e);
                    showNotification({
                        message: `Failed to stop task: ${e.message}`,
                        type: 'error'
                    });
                }
            }
        });
    };

    const handleCopyId = (taskId) => {
        navigator.clipboard.writeText(taskId);
        showNotification({
            message: 'Task ID copied to clipboard',
            type: 'success'
        });
    };

    const renders = [
        {
            title: "State",
            id: "suspended",
            render: (_, task) => {
                if (task.suspended) {
                    return <span className="text-xs border border-admin_warning bg-admin_warning/20 text-admin_warning px-2 py-1 rounded">Suspended</span>;
                }
                return <span className="text-xs border border-admin_success bg-admin_success/20 text-admin_success px-2 py-1 rounded">Active</span>;
            }
        },
        {
            title: "Name",
            id: "name",
            render: (_, task) => (
                <span
                    className="font-medium text-admin_text cursor-pointer hover:text-admin_info transition-colors"
                    onClick={() => {
                        setSelectedTask(task);
                        selectedTaskRef.current = task;
                    }}
                >
                    {task.name}
                </span>
            )
        },
        {
            title: "Uptime",
            id: "uptime",
            render: (_, task) => <LiveUptime task={task} serverTimeOffset={serverTimeOffset} />
        },
        {
            title: "ID",
            id: "id",
            render: (_, task) => (
                <div className="flex items-center gap-1">
                    <span className="text-xs font-mono text-admin_text_secondary" title={task.id}>
                        {task.id.substring(0, 8)}...
                    </span>
                    <button
                        onClick={() => handleCopyId(task.id)}
                        className="p-1 hover:bg-admin_neutral_2 rounded transition-colors"
                        title="Copy full ID"
                    >
                        <ClipboardDocumentIcon className="w-3 h-3 text-admin_text_secondary hover:text-admin_text" />
                    </button>
                </div>
            )
        },
        {
            title: "Status",
            id: "status",
            render: (_, task) => {
                return <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${getStatusColor(task.status)}`}>{task.status}</span>;
            }
        },
        {
            title: "Actions",
            id: "actions",
            render: (_, task) => (
                <div className="flex space-x-1">
                    <Button
                        variant="admin_icon"
                        size="sm"
                        onClick={() => handleStop(task)}
                        title="Stop Task"
                    >
                        <XCircleIcon className="w-5 h-5 text-admin_error" />
                    </Button>
                </div>
            )
        }
    ];

    return (
        <div className="p-6 space-y-6 bg-admin_canvas">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-admin_text">Task Manager</h1>
                <div className="flex items-center gap-3">
                    <Button
                        variant="admin_primary"
                        onClick={() => setShowRunDialog(true)}
                        className="flex items-center gap-2"
                    >
                        <PlayIcon className="w-4 h-4" />
                        Run Task
                    </Button>
                    <Button
                        variant="admin_secondary"
                        onClick={() => setShowGlobalEnvDialog(true)}
                        className="flex items-center gap-2"
                    >
                        <GlobeAltIcon className="w-4 h-4" />
                        Global Environment
                    </Button>
                    <Button
                        variant="admin_secondary"
                        className={`flex items-center space-x-2 border transition-all duration-600 ${
                            connectionStatus === 'connected'
                                ? 'bg-admin_success/10 border-admin_success/30 hover:bg-admin_success/20'
                                : connectionStatus === 'connecting'
                                ? 'bg-admin_warning/10 border-admin_warning/30'
                                : 'bg-admin_error/10 border-admin_error/30'
                        }`}
                        disabled
                    >
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 transition-all duration-600 ${
                            connectionStatus === 'connected'
                                ? 'bg-admin_success'
                                : connectionStatus === 'connecting'
                                ? 'bg-admin_warning animate-pulse'
                                : 'bg-admin_error animate-pulse'
                        }`}></div>
                        <span className={`text-xs transition-colors duration-600 ${
                            connectionStatus === 'connected'
                                ? 'text-admin_success'
                                : connectionStatus === 'connecting'
                                ? 'text-admin_warning'
                                : 'text-admin_error'
                        }`}>
                            {connectionStatus === 'connected'
                                ? 'Connected'
                                : connectionStatus === 'connecting'
                                ? 'Connecting...'
                                : 'Disconnected'}
                        </span>
                    </Button>
                </div>
            </div>

            {isInitialized ? (
                <DataTable
                    localUserPreferencesKey="admin.taskManager.overview"
                    variant="admin_browser"
                    apiMethod={GetTasks}
                    requestFields={requestFields}
                    refreshKey={refreshKey}
                    renders={renders}
                    enableSelection={false}
                    itemsPerPage={20}
                    showSearch={true}
                    searchField="id name status"
                    showFooter={true}
                    preferredHeight="auto"
                    filterControl={
                        <div className="flex items-center gap-2">
                            <div style={{ minWidth: "160px" }}>
                                <DropdownSelect
                                    variant="admin"
                                    options={STATUS_FILTER_OPTIONS}
                                    value={filters.status}
                                    onChange={(values) => setFilters(prev => ({ ...prev, status: values || [] }))}
                                    placeholder="Any Status"
                                    multiSelect={true}
                                />
                            </div>
                            <div style={{ minWidth: "160px" }}>
                                <DropdownSelect
                                    variant="admin"
                                    options={SUSPENDED_FILTER_OPTIONS}
                                    value={filters.suspended}
                                    onChange={(values) => setFilters(prev => ({ ...prev, suspended: values || [] }))}
                                    placeholder="Any"
                                />
                            </div>
                        </div>
                    }
                />
            ) : (
                <div className="flex items-center justify-center p-12">
                    <span className="text-admin_text_secondary">
                        Connecting to task manager...
                    </span>
                </div>
            )}

            {selectedTask && (
                <Modal
                    isOpen={!!selectedTask}
                    onClose={() => {
                        setSelectedTask(null);
                        selectedTaskRef.current = null;
                    }}
                    title="Task Details"
                    variant="admin_drawer_right"
                    closeOnClickOutside={false}
                    passInteractionEvents={true}
                    styleOverrides={{
                        header: "relative flex justify-between items-center px-6 py-6 border-b border-admin_bordered",
                        closeButton: "absolute left-2 top-1/2 -translate-y-1/2 hover:text-admin_text z-10"
                    }}
                >
                    <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
                        <TaskDetailsPanel
                            task={selectedTask}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            serverTimeOffset={serverTimeOffset}
                        />
                    </div>
                </Modal>
            )}

            <RunScriptDialog
                isOpen={showRunDialog}
                onClose={() => setShowRunDialog(false)}
                onRunSuccess={() => {
                    setRefreshKey(prev => prev + 1);
                    setShowRunDialog(false);
                }}
                controller={controller}
            />

            <GlobalEnvDialog
                isOpen={showGlobalEnvDialog}
                onClose={() => setShowGlobalEnvDialog(false)}
                controller={controller}
            />
        </div>
    );
}
